import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { SceneObject } from "../core/SceneObject";
import { getTransitionManager } from "../core/TransitionManager";
import { TraceBehavior, boundPropertyName } from "./tracebehavior";
import { drivesValue } from "./bindingValues";
import { AnimationSequenceData, AnimationStateConfig } from "../scenedata/behaviors/animationsequencedata";
import { Logger } from "../utils/Logger";
import { ValidationError } from "../scenedata/behaviordata";
import { resetTransformToIdentity, ensureRotationQuaternion } from "../utils/TransformUtils";
import { getClipNamesForSrc as getClipNamesForSrcUtil } from "../utils/AnimationUtils";

type StateName = 'enter' | 'main' | 'exit';

const STATE_NAMES: readonly StateName[] = ['enter', 'main', 'exit'];

/** AnimationGroup names, kept stable — they surface in logs and scene dumps. */
const GROUP_NAMES: Record<StateName, string> = {
    enter: 'EnterAnimation', main: 'MainAnimation', exit: 'ExitAnimation',
};

/** Private accessor passed from AnimationSequence to its handles. Not exported. */
interface _StateAccessor {
    getResolved(state: StateName): AnimationStateConfig | undefined;
    setSpeed(state: StateName, speed: number): number;
    commitState(state: StateName, patch: Partial<AnimationStateConfig>): Promise<void>;
    clearState(state: StateName): Promise<void>;
    stopState(state: StateName): void;
    poseState(state: StateName): void;
    playState(state: StateName): Promise<void>;
    getClipNamesForSrc(src: string): Promise<string[]>;
}

/**
 * Lightweight handle for reading/writing a single animation state (enter, main, or exit).
 *
 * Created via `AnimationSequence.getEnter()`, `.getMain()`, `.getExit()`.
 * Not cached — create a new one each time (it's just two fields + an accessor ref).
 *
 * All methods are safe when no state is configured:
 * - Read getters return `undefined` / `1.0` / `true`
 * - `setSpeed()` is a no-op, returns `1.0`
 * - `setSrc()` creates the state via commit
 * - `clear()`, `stop()`, `play()`, `pose()` are no-ops
 */
export class AnimationStateHandle {
    /** @internal */
    constructor(
        private readonly _access: _StateAccessor,
        private readonly _state: StateName,
    ) {}

    // ── Read (from resolved data) ──

    /** Source URL/path to the animation GLB file */
    get src(): string | undefined {
        return this._access.getResolved(this._state)?.src;
    }

    /** Animation clip name within the GLB */
    get clip(): string | undefined {
        return this._access.getResolved(this._state)?.clip;
    }

    /** Playback speed (defaults to 1.0) */
    get speed(): number {
        return this._access.getResolved(this._state)?.speed ?? 1.0;
    }

    /** Delay in seconds before auto-triggered playback (defaults to 0) */
    get delay(): number {
        return this._access.getResolved(this._state)?.delay ?? 0;
    }

    /** True when no src is set (state is unconfigured) */
    get isEmpty(): boolean {
        return !this.src;
    }

    // ── Write ──

    /**
     * Set playback speed synchronously.
     * Validates/clamps (must be > 0), writes directly to data and applies
     * to the live AnimationGroup immediately. No commit cycle.
     *
     * @returns The actual speed value used (e.g. clamped). Returns 1.0 if state is empty.
     */
    setSpeed(speed: number): number {
        return this._access.setSpeed(this._state, speed);
    }

    /**
     * Set the animation source (and optional clip). Async — triggers commit
     * and GLB hot-reload.
     *
     * Creates the state if it doesn't exist.
     */
    async setSrc(src: string, clip?: string): Promise<void> {
        await this._access.commitState(this._state, clip ? { src, clip } : { src });
    }

    /** Remove this state entirely. No-op if already empty. */
    async clear(): Promise<void> {
        if (this.isEmpty) return;
        await this._access.clearState(this._state);
    }

    // ── Playback ──

    /** Stop this state's playback. No-op if not playing or no group loaded. */
    stop(): void {
        this._access.stopState(this._state);
    }

    /**
     * Pose this state's clip at its first frame, paused, so the frame is
     * visually applied before playback starts (e.g. a staggered page entrance
     * holding its authored "hidden" pose during its delay window). `play()`
     * resumes from the posed frame. No-op if no group is loaded.
     */
    pose(): void {
        this._access.poseState(this._state);
    }

    /** Play this state's animation. Resolves when the animation ends (or immediately if no group). */
    play(): Promise<void> {
        return this._access.playState(this._state);
    }

    // ── Discovery ──

    /** Get available clip names from this state's GLB source. Returns [] if no src. */
    async getClipNames(): Promise<string[]> {
        const src = this.src;
        if (!src) return [];
        return this._access.getClipNamesForSrc(src);
    }
}

/**
 * AnimationSequence
 *
 * Loads external animation clips and plays them across the SceneObject's
 * animation nodes with an enter → main → exit state machine: sequence
 * transitions (enter/exit) drive the animation LAYER while the
 * while-active loop drives the animation root underneath, so the phases
 * compose per channel instead of fighting over one transform.
 *
 * Each state references an external GLB file and an optional clip name.
 * Enter/exit play once; main loops.
 *
 * ## State Handle API
 *
 * Use `getEnter()`, `getMain()`, `getExit()` to get typed handles for
 * individual states:
 *
 * ```typescript
 * const main = animSeq.getMain();
 * main.speed;                         // read current speed
 * main.setSpeed(2);                   // sync — immediate, returns clamped value
 * await main.setSrc('walk.glb', 'Walk'); // async — triggers GLB reload
 * await main.clear();                 // async — removes state
 * main.play();                        // play this state
 * main.stop();                        // stop this state
 * ```
 */
export class AnimationSequence extends TraceBehavior<AnimationSequenceData> {
    static readonly behaviorType = "AnimationSequence" as const;

    // Runtime state (not serialized)
    private enterGroup: AnimationGroup | null = null;
    private mainGroup: AnimationGroup | null = null;
    private exitGroup: AnimationGroup | null = null;

    /** Currently playing state */
    private _currentState: 'idle' | 'enter' | 'main' | 'exit' = 'idle';

    /** Pending delayed sequence start (`playSequence` with an authored enter
     *  delay). Cancelled by any playback change (`_stopAll`), including
     *  `stop()` and leaving play mode. Holds the timer's cancel. */
    private _enterDelayTimer: (() => void) | null = null;

    /** Pending delayed departure (`requestExit` with an authored exit delay).
     *  Cancelled by any playback change; the pending promise is RESOLVED on
     *  cancellation (without playing) so orchestrator barriers never hang on
     *  a superseded departure. Holds the timer's cancel. */
    private _exitDelayTimer: (() => void) | null = null;
    private _exitDelayResolve: (() => void) | null = null;

    /** The current sequence run's enter-end listener (the chain handoff).
     *  Removed explicitly on any playback change —
     *  a superseded run's listener must not survive to fire-order luck.
     *  NEVER cleared via observable.clear(): the enter observable also
     *  carries handle-play promise resolvers owned by other callers. */
    private _enterEndObserver: Observer<AnimationGroup> | null = null;

    constructor(sceneObject: SceneObject, data: AnimationSequenceData) {
        super(sceneObject, data);
        this.validate(this.getAuthored());
        this._resolved = this._resolveAll();
    }

    /**
     * Validate animation sequence data.
     * Mutates data in-place for migrations/normalization.
     */
    protected validate(data: AnimationSequenceData): ValidationError[] {
        const errors: ValidationError[] = [];

        if (!data.states) {
            data.states = {};
        }

        const stateNames = ['enter', 'main', 'exit'] as const;
        for (const stateName of stateNames) {
            const state = data.states[stateName];
            if (state) {
                if (state.src && typeof state.src !== 'string') {
                    errors.push({ field: `states.${stateName}.src`, message: 'Animation source must be a string', code: 'INVALID_SRC' });
                }
                if (state.clip && typeof state.clip !== 'string') {
                    errors.push({ field: `states.${stateName}.clip`, message: 'Clip name must be a string', code: 'INVALID_CLIP' });
                }
                if (state.speed !== undefined && (typeof state.speed !== 'number' || state.speed <= 0)) {
                    errors.push({ field: `states.${stateName}.speed`, message: 'Speed must be a positive number', code: 'INVALID_SPEED' });
                }
            }
        }

        return errors;
    }

    /**
     * Resolve authored refs, then re-resolve each slot's bound `src` and clip
     * NAME from the SETTLED (target-state) value so the two swap ATOMICALLY.
     *
     * A slot's src (`$animEnter`) and name (`$clipname_enter`) are two discrete
     * Properties. During a state transition the generic resolver returns each
     * one's mid-transition overlay, which flips them on SEPARATE ticks — so a
     * reload triggered by the src flip pairs the new GLB with the old clip name
     * → "clip not found" → jagged enter/exit. Reading the settled value gives
     * both slots the target state's value at once (the swap is discrete, not a
     * crossfade), and leaves the settled result constant across the transition
     * so it drives exactly ONE reload — at the state switch — with no timing
     * change to the editor (no transition overlay there → settled == live).
     */
    protected _resolveAll(): AnimationSequenceData {
        const resolved = super._resolveAll();
        const states = resolved.states;
        if (!states) return resolved;
        const authoredStates = this.getAuthored().states;
        for (const state of STATE_NAMES) {
            const authored = authoredStates?.[state];
            const slot = states[state];
            if (!authored || !slot) continue;
            const src = this._resolveSettledRef(authored.src);
            if (src !== undefined) slot.src = src;
            const clip = this._resolveSettledRef(authored.clip);
            if (clip !== undefined) slot.clip = clip;
        }
        return resolved;
    }

    /**
     * Resolve a bound `$name`/`{name}` field from the SETTLED value (skips the
     * transition overlay). Returns undefined for a literal or an unresolvable
     * ref, so the caller keeps the generic resolution.
     */
    private _resolveSettledRef(field: unknown): string | undefined {
        if (typeof field !== 'string') return undefined;
        const name = boundPropertyName(field);
        if (!name) return undefined; // literal — keep the base-resolved value
        const settled = this.sceneObject.resolveProperty(name, true);
        return typeof settled === 'string' ? settled : undefined;
    }

    protected async applyCommit(_patch: Partial<AnimationSequenceData>, prev: AnimationSequenceData): Promise<void> {
        const prevStates = prev.states ?? {};
        const newStates = this._resolved.states ?? {};

        const enterChanged = newStates.enter?.src !== prevStates.enter?.src
            || newStates.enter?.clip !== prevStates.enter?.clip;
        const mainChanged = newStates.main?.src !== prevStates.main?.src
            || newStates.main?.clip !== prevStates.main?.clip;
        const exitChanged = newStates.exit?.src !== prevStates.exit?.src
            || newStates.exit?.clip !== prevStates.exit?.clip;

        if (enterChanged || mainChanged || exitChanged) {
            this.stop();
            await this._hotReloadGroups(enterChanged, mainChanged, exitChanged);

            // State-driven clip swap: a bound `src` resolved to a new value
            // (e.g. a state activated and overrode `{animMain}`). While playing,
            // restart the sequence so the new clip takes over immediately. Gated
            // on play mode so editor-time inspector edits don't auto-play.
            if (this.playMode && this._hasAnyClip()) {
                this.playSequence();
            }
            return;
        }

        // No src/clip change — apply speed updates to active groups immediately
        this._applySpeeds();
    }

    async load(): Promise<void> {
        // Ensure animation root exists — SceneObject owns it, we just request it
        const animRoot = this.sceneObject.getOrCreateAnimationRoot();
        resetTransformToIdentity(animRoot, '[AnimationSequence]');

        for (const state of STATE_NAMES) {
            const config = this._resolved.states[state];
            // `drivesValue`, not truthiness: a bound clip src (`$heroClip`,
            // the animation-clip declaration kind) that no provider declares
            // survives resolution as its own text, and loading THAT fetches a
            // URL that cannot exist. Nothing drives → no clip, quietly.
            if (!drivesValue(config?.src)) continue;
            this._setGroup(state, await this._loadClip(
                config.src, GROUP_NAMES[state], config.clip, this._clipTargetFor(state),
            ));
        }

        // After the loads — the layer is created lazily BY them, so resetting
        // earlier would be a no-op on first load.
        this._resetLayer();

        Logger.debug(`AnimationSequence: clips ready on ${this.sceneObject.name} (no auto-play)`);
    }

    protected onAttach(): void {
        Logger.debug("[AnimationSequence] attach");
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            // Play-mode entry is just the first activation event: only
            // effectively-active objects play. Objects on hidden pages (or
            // under any disabled ancestor) start their sequence when they
            // actually activate — their loops no longer run invisibly from
            // t=0 (historically they did; see PAGES_STATES_UNIFICATION_PLAN).
            if (this.sceneObject.effectiveActive) {
                this.playSequence();
            }
        } else {
            this.stop();
        }
    }

    /**
     * Activation lifecycle (play mode only): the authored slots ARE the
     * declaration — the enter slot has always been documented as "plays once
     * when object enters", and effective activation is when the object
     * enters, no matter who activated it (a state's `active`, a page flip,
     * a script). Deactivation is an instant hide from this behavior's point
     * of view: exits are only VISIBLE under a deferred hide, which an
     * orchestrator requests via {@link requestExit} BEFORE hiding — so a
     * plain deactivation just stops playback.
     *
     * A state-driven clip swap in the same page flip triggers a second
     * playSequence via applyCommit's hot-reload — that restart supersedes
     * this one and plays the NEW clip (last intent wins; the supersede
     * machinery is the same one that guards user-driven restarts).
     */
    protected override onEffectiveActiveChanged(active: boolean): void {
        if (!this.playMode) return;
        if (active) {
            if (this._hasAnyClip()) this.playSequence();
        } else {
            this.stop();
        }
    }

    /**
     * Play the authored exit as an awaited departure: honors the exit slot's
     * `delay`, then plays the clip; the returned promise covers delay +
     * playback. For orchestrators sequencing a deferred hide (an
     * `'exitFirst'` page change, an awaited state deactivation) — call this
     * BEFORE hiding the object. Resolves immediately when no exit is
     * authored; resolves (without playing) when a playback change supersedes
     * the pending delayed start, so barriers never hang on a superseded
     * departure.
     */
    public requestExit(): Promise<void> {
        const exit = this.getExit();
        if (exit.isEmpty) return Promise.resolve();
        const delay = exit.delay;
        if (delay <= 0) return exit.play();
        return new Promise<void>((resolve) => {
            this._exitDelayResolve = resolve;
            this._exitDelayTimer = getTransitionManager(this.scene).after(delay * 1000, () => {
                this._exitDelayTimer = null;
                this._exitDelayResolve = null;
                void exit.play().then(resolve);
            });
        });
    }

    protected onDetach(): void {
        Logger.debug("[AnimationSequence] detach");
        this.stop();

        // Dispose animation groups
        for (const g of [this.enterGroup, this.mainGroup, this.exitGroup]) {
            if (!g) continue;
            try { g.dispose(); } catch (e) {
                Logger.warn(`[AnimationSequence] Failed to dispose group "${g.name}": ${String(e)}`);
            }
        }
        this.enterGroup = this.mainGroup = this.exitGroup = null;

        // Reset animation root to identity (SceneObject owns it, we don't dispose it)
        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            resetTransformToIdentity(animRoot, '[AnimationSequence]');
        }
        this._resetLayer();
    }

    // ============================
    // Public Playback API
    // ============================

    /**
     * Stop all animation playback and reset animation root to identity.
     */
    public stop(): void {
        this._stopAll();
        this._currentState = 'idle';

        // Reset animation root to initial pose
        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            resetTransformToIdentity(animRoot, '[AnimationSequence]');
        }
        this._resetLayer();
    }

    /**
     * Check if animation is currently playing.
     */
    public isPlaying(): boolean {
        return this._currentState !== 'idle';
    }

    /**
     * Get current animation state.
     */
    public getCurrentState(): 'idle' | 'enter' | 'main' | 'exit' {
        return this._currentState;
    }

    // ============================
    // State Handle API
    // ============================

    /** Accessor object shared by all handles. Created lazily, never exported. */
    private _accessor: _StateAccessor | null = null;

    private _getAccessor(): _StateAccessor {
        if (!this._accessor) {
            this._accessor = {
                getResolved: (state) => this._resolved.states?.[state],
                setSpeed: (state, speed) => this._setStateSpeed(state, speed),
                commitState: (state, patch) => this._commitState(state, patch),
                clearState: (state) => this._clearState(state),
                stopState: (state) => this._stopState(state),
                poseState: (state) => this._poseState(state),
                playState: (state) => this._playState(state),
                getClipNamesForSrc: (src) => this.getClipNamesForSrc(src),
            };
        }
        return this._accessor;
    }

    /** Get a handle for the enter state */
    public getEnter(): AnimationStateHandle { return new AnimationStateHandle(this._getAccessor(), 'enter'); }

    /** Get a handle for the main state */
    public getMain(): AnimationStateHandle { return new AnimationStateHandle(this._getAccessor(), 'main'); }

    /** Get a handle for the exit state */
    public getExit(): AnimationStateHandle { return new AnimationStateHandle(this._getAccessor(), 'exit'); }

    // ── Private handle implementation ──

    private _setStateSpeed(state: StateName, speed: number): number {
        const stateConfig = this._resolved.states?.[state];
        if (!stateConfig) return 1.0;

        const clamped = Math.max(speed, 0.01);

        // Write through to both authored and resolved data
        const authored = this.getAuthored();
        if (authored.states?.[state]) {
            authored.states[state].speed = clamped;
        }
        stateConfig.speed = clamped;

        // Apply to live group immediately
        const group = this._getGroup(state);
        if (group) {
            group.speedRatio = clamped;
        }

        return clamped;
    }

    private async _commitState(state: StateName, patch: Partial<AnimationStateConfig>): Promise<void> {
        const authoredStates = this.getAuthored().states ?? {};
        const currentStateConfig = authoredStates[state] ?? {};
        await this.commit({
            states: {
                ...authoredStates,
                [state]: { ...currentStateConfig, ...patch },
            },
        });
    }

    private async _clearState(state: StateName): Promise<void> {
        const newStates = { ...this.getAuthored().states };
        delete newStates[state];
        await this.commit({ states: newStates });
    }

    private _stopState(state: StateName): void {
        const group = this._getGroup(state);
        this._stopGroup(group);
        if (this._currentState === state) {
            this._currentState = 'idle';
            const animRoot = this.sceneObject.animationRoot;
            if (animRoot) {
                resetTransformToIdentity(animRoot, '[AnimationSequence]');
            }
            this._resetLayer();
        }
    }

    private _poseState(state: StateName): void {
        const group = this._getGroup(state);
        if (!group) return;
        this._stopAll(group);
        this._currentState = state;
        this._poseGroupAtStart(group, this._speedFor(state));
    }

    private _playState(state: StateName): Promise<void> {
        switch (state) {
            case 'enter': return this._playEnter();
            case 'main': return this._playMain();
            case 'exit': return this._playExit();
        }
    }

    private _getGroup(state: StateName): AnimationGroup | null {
        switch (state) {
            case 'enter': return this.enterGroup;
            case 'main': return this.mainGroup;
            case 'exit': return this.exitGroup;
        }
    }

    /**
     * Install pre-built AnimationGroups without going through a GLB load.
     *
     * @internal Test seam. Loading real clips needs GLB fixtures plus a live
     * ResourceManager, so the suites build dummy groups instead — and before
     * this existed every one of them reached in with
     * `(animSeq as any).enterGroup = ...`, which welded ~30 assertions to
     * private field names and disabled the no-explicit-any lint across a
     * 1000-line file. Production code must go through commit() /
     * _hotReloadGroups; this exists so the internals stay renameable.
     */
    setGroupsForTest(groups: { enter?: AnimationGroup | null; main?: AnimationGroup | null; exit?: AnimationGroup | null }): void {
        for (const state of STATE_NAMES) {
            if (state in groups) this._setGroup(state, groups[state] ?? null);
        }
    }

    private _setGroup(state: StateName, group: AnimationGroup | null): void {
        switch (state) {
            case 'enter': this.enterGroup = group; break;
            case 'main': this.mainGroup = group; break;
            case 'exit': this.exitGroup = group; break;
        }
    }

    /**
     * Get available clip names from an external GLB source.
     * Resolves local asset paths via ResourceManager. Results are cached.
     */
    public async getClipNamesForSrc(src: string): Promise<string[]> {
        return getClipNamesForSrcUtil(src, this.scene, this.resources);
    }

    /**
     * Play the full enter → main sequence.
     * Enter plays once, then automatically transitions to main (loop).
     *
     * An authored enter `delay` holds the sequence at the enter clip's first
     * frame (the authored "hidden" pose) and starts it after the delay — so
     * staggered entrances also stagger on play-mode start and on state-driven
     * clip swaps, not only on page flips. The delay belongs to the TRIGGER
     * that initiates playback: `playSequence` honors it here, the Pages
     * fan-out schedules its own timers, and `handle.play()` stays immediate
     * (the P-18 awaited-exit contract depends on that).
     */
    public playSequence(): void {
        // Show-time rule: (re)starting the sequence always cancels a running
        // exit AND a pending delayed departure — exits belong to hide-time
        // (deactivation, page departure); a re-show during the exit-delay
        // window must not have the departure fire afterwards.
        this._clearExitDelayTimer();
        this._stopGroup(this.exitGroup);
        // Without an enter clip to re-drive the sequence layer from its own
        // first frame, a previous entrance's or exit's final pose would
        // survive on it — reset the layer so the object shows at steady state.
        if (!this.enterGroup) this._resetLayer();

        const enterDelay = this._resolved.states.enter?.delay ?? 0;

        if (this.enterGroup && enterDelay > 0) {
            // _poseState stops other groups and clears any pending delayed
            // start before the new timer is armed. The loop is then pre-posed
            // at its first frame UNDER the held entrance, so the hold never
            // shows a stale mid-loop pose on the root.
            this._poseState('enter');
            this._poseMainUnderEnter();
            this._enterDelayTimer = getTransitionManager(this.scene).after(enterDelay * 1000, () => {
                this._enterDelayTimer = null;
                this._startSequencePlayback();
            });
            return;
        }
        this._startSequencePlayback();
    }

    /** Start a group paused at its first frame so the frame is applied. */
    private _poseGroupAtStart(group: AnimationGroup, speed: number): void {
        if (!group.isStarted) {
            group.start(false, speed);
        }
        group.pause();
        group.goToFrame(group.from);
        group.setWeightForAllAnimatables(1);
    }

    /**
     * Pre-pose the loop at its first frame on the animation root (started
     * paused) so a chained entrance composes over EXACTLY the pose the loop
     * will start from — the chain handoff cannot snap, and a stale mid-loop
     * pose left frozen on the root (the loop runs from play-mode start even
     * on hidden pages) is cleared at entrance start.
     */
    private _poseMainUnderEnter(): void {
        if (this.mainGroup) {
            this._poseGroupAtStart(this.mainGroup, this._speedFor('main'));
        }
    }

    /**
     * Resume the while-active loop — via play(), not start(): the chained
     * loop sits pre-posed started-paused, and Babylon's start() early-returns
     * on a started group; play() unpauses it from the posed frame (and falls
     * back to start when it was never posed). The ONE handoff used by both
     * the chain-end callback and the exit-interrupts-entrance path, so the
     * two cannot drift.
     */
    private _resumeMainLoop(): void {
        if (!this.mainGroup) return;
        this.mainGroup.setWeightForAllAnimatables(1);
        this.mainGroup.speedRatio = this._speedFor('main');
        this.mainGroup.play(true);
    }

    /**
     * Show-time steady state WITHOUT entrance playback: cancel a running
     * exit, reset the sequence layer a departure stained, and ensure the
     * while-active loop runs — `playSequence`'s enter-less behavior given a
     * name. Used to repair an in-flight departure (e.g. navigating back to a
     * page whose exit cascade already started): exited objects must be
     * repaired either way, but an authored enter clip must not play
     * un-opted. A loop that is ALREADY playing keeps its phase — repair
     * must be jump-free for objects that didn't need repairing.
     */
    public restoreSteadyState(): void {
        this._clearExitDelayTimer();
        this._stopGroup(this.exitGroup);
        this._resetLayer();
        if (this.mainGroup?.isPlaying) {
            this._currentState = 'main';
        } else if (this.mainGroup) {
            void this._playMain();
        } else {
            this._currentState = 'idle';
        }
    }

    /** Immediate sequence start — the pre-delay body of {@link playSequence}. */
    private _startSequencePlayback(): void {
        const hasEnter = !!this.enterGroup;
        const hasMain = !!this.mainGroup;

        if (hasEnter && hasMain) {
            Logger.debug(`[AnimationSequence] Playing enter → main sequence for ${this.sceneObject.name}`);
            this._stopAll();
            this._currentState = 'enter';

            const enterGroup = this.enterGroup!;

            // The chain handoff cannot snap: the loop is pre-posed at its
            // first frame under the entrance and RESUMED (not restarted)
            // when the entrance completes.
            this._poseMainUnderEnter();

            this._armGroup(enterGroup, 'enter');

            this._enterEndObserver = enterGroup.onAnimationGroupEndObservable.addOnce(() => {
                this._enterEndObserver = null;
                if (this._currentState !== 'enter') return;

                Logger.debug(`[AnimationSequence] Enter complete, transitioning to main for ${this.sceneObject.name}`);
                this._currentState = 'main';
                // The completed entrance must not leave its final frame on
                // the layer — the loop owns steady state (chain-end seam).
                this._resetLayer();

                this._resumeMainLoop();
            });

            enterGroup.play(false);

        } else if (hasEnter) {
            void this._playEnter();
        } else if (hasMain) {
            void this._playMain();
        } else if (this.exitGroup) {
            // Exit-only: nothing plays at show-time — the running exit was
            // cancelled and the layer reset in playSequence; the clip's job
            // is the departure.
            this._currentState = 'idle';
        } else {
            Logger.warn(`[AnimationSequence] No animations available for ${this.sceneObject.name}`);
        }
    }

    // ── Per-state playback (private — use handle.play() or playSequence()) ──

    private _playEnter(): Promise<void> {
        if (!this.enterGroup) return Promise.resolve();
        this._stopAll(this.enterGroup);
        this._currentState = 'enter';

        this._armGroup(this.enterGroup, 'enter');

        return new Promise<void>((resolve) => {
            this.enterGroup!.onAnimationGroupEndObservable.addOnce(() => {
                if (this._currentState === 'enter') {
                    this._currentState = 'idle';
                }
                resolve();
            });
            this.enterGroup!.play(false);
        });
    }

    private _playMain(): Promise<void> {
        if (!this.mainGroup) return Promise.resolve();
        this._stopAll(this.mainGroup);
        this._currentState = 'main';

        this._armGroup(this.mainGroup, 'main');

        this.mainGroup.start(true);
        // Main loops — resolve immediately (caller doesn't await loop end)
        return Promise.resolve();
    }

    private _playExit(): Promise<void> {
        if (!this.exitGroup) return Promise.resolve();
        // Layered composition: the exit drives the sequence layer while the
        // while-active loop keeps playing on the animation root underneath —
        // the live pose is carried off, never replaced, so the handoff cannot
        // jump. Only a running entrance (and a pending delayed sequence
        // start) is stopped.
        this._clearEnterDelayTimer();
        // An interrupted entrance hands off to the loop EXPLICITLY. The chain
        // listener is detached before the stop — Babylon fires the end
        // observable synchronously from stop(), and the handoff must not
        // depend on that fire-order — so the work it would have done
        // (clear the entrance's layer residue, run the loop under the
        // departing exit) is done here by hand. This also covers an
        // entrance still in its delay-window hold, which never had a
        // chain listener at all.
        const entranceInterrupted = this._currentState === 'enter';
        this._clearEnterEndObserver();
        this._stopGroup(this.enterGroup);
        if (entranceInterrupted) {
            // Clear the entrance's residue even with NO main slot: the exit
            // arms only the channels it animates, so an un-reset layer would
            // carry the entrance's last pose on every other channel for the
            // whole departure.
            this._resetLayer();
            this._resumeMainLoop();
        }
        this._currentState = 'exit';

        this._armGroup(this.exitGroup, 'exit');

        return new Promise<void>((resolve) => {
            this.exitGroup!.onAnimationGroupEndObservable.addOnce(() => {
                if (this._currentState === 'exit') {
                    // The exit hands back to the loop still running underneath.
                    this._currentState = this.mainGroup?.isPlaying ? 'main' : 'idle';
                    Logger.debug(`[AnimationSequence] Exit complete for ${this.sceneObject.name}`);
                }
                resolve();
            });
            this.exitGroup!.play(false);
        });
    }

    /**
     * The node a slot's clip animates. Sequence transitions (enter/exit) drive
     * the animation LAYER — created lazily, so only objects with them pay
     * for the extra node — while the while-active loop drives the animation
     * root underneath. Nested transforms compose per channel (positions add
     * through the hierarchy, rotations and scales multiply), so phase
     * handoffs cannot jump.
     */
    private _clipTargetFor(state: StateName): TransformNode {
        if (state === 'main') {
            return this.sceneObject.animationRoot || this.sceneObject.transform;
        }
        return this.sceneObject.getOrCreateAnimationLayer();
    }

    /** Authored speed for a state, defaulting to 1x. */
    private _speedFor(state: StateName): number {
        return this._resolved.states[state]?.speed ?? 1.0;
    }

    /**
     * Rewind a group to its start, give it full weight and apply its authored
     * speed — the three things every playback entry point did by hand. Callers
     * still choose how to run it afterwards (play/pause/loop), which is the
     * only part that actually differs between them.
     */
    private _armGroup(group: AnimationGroup, state: StateName): void {
        group.reset();
        group.setWeightForAllAnimatables(1);
        group.speedRatio = this._speedFor(state);
    }

    /**
     * Stop a group, tolerating one that Babylon has already disposed.
     *
     * A disposed AnimationGroup throws from stop(), and this behavior holds
     * group references across scene teardown and hot-reloads, so every stop
     * site has to cope. Funnelling them through here keeps that knowledge in
     * one place instead of five try/catch blocks — and, unlike the bare
     * `catch {}` they replace, an error that ISN'T a disposed group still
     * gets surfaced rather than silently swallowed.
     */
    private _stopGroup(group: AnimationGroup | null | undefined): void {
        if (!group) return;
        try {
            group.stop();
        } catch (e) {
            // `_scene` is nulled on dispose; anything else is a real failure.
            const disposed = !(group as unknown as { _scene?: unknown })._scene;
            if (!disposed) {
                Logger.warn(`[AnimationSequence] Failed to stop group "${group.name}": ${String(e)}`);
            }
        }
    }

    /** Reset the animation layer to identity, if one exists. */
    private _resetLayer(): void {
        const layer = this.sceneObject.animationLayer;
        if (layer) {
            resetTransformToIdentity(layer, '[AnimationSequence]');
        }
    }

    // ---------- internal ----------

    /**
     * Hot-reload changed animation groups without a full SceneObject reload.
     * Disposes old groups for changed states and loads replacements in-place.
     */
    private async _hotReloadGroups(enterChanged: boolean, mainChanged: boolean, exitChanged: boolean): Promise<void> {
        // Ensure animation root exists
        this.sceneObject.getOrCreateAnimationRoot();

        const states = this._resolved.states ?? {};
        const changed: Record<StateName, boolean> = {
            enter: enterChanged, main: mainChanged, exit: exitChanged,
        };

        for (const state of STATE_NAMES) {
            if (!changed[state]) continue;

            const existing = this._getGroup(state);
            if (existing) {
                try { existing.dispose(); } catch (e) {
                    Logger.warn(`[AnimationSequence] Failed to dispose ${state} group: ${String(e)}`);
                }
            }
            this._setGroup(state, null);

            const config = states[state];
            // `drivesValue`, not truthiness: a bound clip src (`$heroClip`,
            // the animation-clip declaration kind) that no provider declares
            // survives resolution as its own text, and loading THAT fetches a
            // URL that cannot exist. Nothing drives → no clip, quietly.
            if (!drivesValue(config?.src)) continue;
            this._setGroup(state, await this._loadClip(
                config.src, GROUP_NAMES[state], config.clip, this._clipTargetFor(state),
            ));
        }

        Logger.debug(`[AnimationSequence] Hot-reloaded animation groups for ${this.sceneObject.name}`);
    }

    /** True if any of the three states currently has a loaded group. */
    private _hasAnyClip(): boolean {
        return !!(this.enterGroup || this.mainGroup || this.exitGroup);
    }

    /** Apply resolved speeds to any currently-playing groups. */
    private _applySpeeds(): void {
        if (this.enterGroup) this.enterGroup.speedRatio = this._speedFor('enter');
        if (this.mainGroup) this.mainGroup.speedRatio = this._speedFor('main');
        if (this.exitGroup) this.exitGroup.speedRatio = this._speedFor('exit');
    }

    /** Cancel a pending delayed sequence start (`playSequence` enter delay). */
    private _clearEnterDelayTimer(): void {
        this._enterDelayTimer?.();
        this._enterDelayTimer = null;
    }

    /** Cancel a pending delayed departure, resolving its promise (no play). */
    private _clearExitDelayTimer(): void {
        this._exitDelayTimer?.();
        this._exitDelayTimer = null;
        if (this._exitDelayResolve !== null) {
            const resolve = this._exitDelayResolve;
            this._exitDelayResolve = null;
            resolve();
        }
    }

    /** Detach the current run's enter-end listener, if any. */
    private _clearEnterEndObserver(): void {
        if (this._enterEndObserver !== null) {
            this.enterGroup?.onAnimationGroupEndObservable.remove(this._enterEndObserver);
            this._enterEndObserver = null;
        }
    }

    /** Stop all animation groups except the one optionally specified. */
    private _stopAll(except?: AnimationGroup): void {
        // Any playback change cancels a pending delayed sequence start, a
        // pending delayed departure, and the superseded run's enter-end
        // listener.
        this._clearEnterDelayTimer();
        this._clearExitDelayTimer();
        this._clearEnterEndObserver();
        for (const g of [this.enterGroup, this.mainGroup, this.exitGroup]) {
            if (g !== except) this._stopGroup(g);
        }
    }

    /**
     * Load an external GLB, pick the requested clip, and retarget all its
     * animations onto the animation root.
     */
    private async _loadClip(src: string, groupName: string, specificClipName?: string, target?: TransformNode): Promise<AnimationGroup | null> {
        // Load through the ResourceManager's refcounted container cache (shared
        // with getClipNamesForSrc), so a slot's clip play reuses the GLB already
        // parsed for its clip-name split instead of re-fetching+re-parsing it.
        // We only READ the container's groups (cloning each animation onto our
        // own retargeted group), never mutate them, so a shared container is
        // safe. Release our ref exactly once in `finally`; the names-load's
        // held ref keeps the container cached for reuse.
        let acquired = false;
        try {
            const container = await this.resources.getOrLoadContainer(src, this.scene);
            acquired = true;

            if (!container.animationGroups.length) {
                Logger.warn(`AnimationSequence: no groups in ${src}`);
                return null;
            }

            // Pick the requested clip or fall back to the first one
            let sourceGroup: AnimationGroup | undefined;
            if (specificClipName) {
                sourceGroup = container.animationGroups.find(g => g.name === specificClipName);
                if (!sourceGroup) {
                    Logger.warn(`AnimationSequence: clip "${specificClipName}" not found in ${src}. Available:`,
                        container.animationGroups.map(g => g.name));
                    return null;
                }
            } else {
                sourceGroup = container.animationGroups[0];
            }

            const root = target ?? (this.sceneObject.animationRoot || this.sceneObject.transform);

            // A clip that animates rotationQuaternion needs the target to
            // HAVE one, or Babylon's reset-before-replay restores a captured
            // null and crashes (page rendered empty). Fresh runtime nodes
            // (animation root/layer) are Euler-mode by default; clips that
            // don't animate rotation leave Euler mode untouched.
            if (sourceGroup.targetedAnimations.some(ta => ta.animation.targetProperty === 'rotationQuaternion')) {
                ensureRotationQuaternion(root);
            }

            const retargeted = new AnimationGroup(groupName, this.scene);

            for (const ta of sourceGroup.targetedAnimations) {
                retargeted.addTargetedAnimation(ta.animation.clone(), root);
            }

            return retargeted;
        } catch (e) {
            Logger.error(`AnimationSequence: failed to load ${src}: ${String(e)}`);
            return null;
        } finally {
            if (acquired) this.resources.releaseContainer(src);
        }
    }
}
